import { useEffect, useMemo, useRef } from 'react';
import { useLeafletMap } from '@/lib/leaflet/useLeafletMap';
import type { LeafletMap, MapLayer, TileLayer } from '@/lib/leaflet/leafletMap';
import type { GeoPoint } from '@/signalr';
import { CONTEXT_MENU_CHANGE_CALLER_LOCATION } from './mapModel';
import type { ContextMenuItem, LatLng, MapCircle, MapPath } from './mapModel';
import type { MapMarker } from './markers';

export type LeafletMapViewProps = {
  center?: GeoPoint | null;
  zoom?: number | null;
  markers?: readonly MapMarker[];
  circles?: readonly MapCircle[];
  paths?: readonly MapPath[];
  disableControls?: boolean;
  onMarkerClicked?: (markerId: string) => void;
  contextMenuItems?: readonly ContextMenuItem[];
  onChangeCallerLocation?: (position: LatLng) => void;
  agencyLayers?: readonly MapLayer[];
  tileLayers?: readonly TileLayer[];
  hideBuiltInLayerControl?: boolean;
  onMapReady?: (map: LeafletMap | null) => void;
};

const DEFAULT_CENTER: GeoPoint = { latitude: 50.0755, longitude: 14.4378 };
const DEFAULT_ZOOM = 13;

export function LeafletMapView({
  center,
  zoom,
  markers = [],
  circles = [],
  paths = [],
  disableControls = false,
  onMarkerClicked,
  contextMenuItems = [],
  onChangeCallerLocation,
  agencyLayers = [],
  tileLayers = [],
  hideBuiltInLayerControl = false,
  onMapReady,
}: LeafletMapViewProps) {
  const onMarkerClickedRef = useRef(onMarkerClicked);
  const onChangeCallerLocationRef = useRef(onChangeCallerLocation);
  onMarkerClickedRef.current = onMarkerClicked;
  onChangeCallerLocationRef.current = onChangeCallerLocation;

  const { containerRef, map } = useLeafletMap({
    markerClicked: (markerId) => {
      onMarkerClickedRef.current?.(markerId);
    },
    contextMenuItemClicked: (itemId, position) => {
      if (itemId === CONTEXT_MENU_CHANGE_CALLER_LOCATION) {
        onChangeCallerLocationRef.current?.({ lat: position.lat, lng: position.lng });
      }
    },
  });

  const onMapReadyRef = useRef(onMapReady);
  onMapReadyRef.current = onMapReady;
  useEffect(() => {
    onMapReadyRef.current?.(map ?? null);
  }, [map]);

  const lastViewRef = useRef<string | null>(null);
  const effectiveCenter = center ?? null;
  const effectiveZoom = zoom ?? null;
  useEffect(() => {
    if (!map) {
      return;
    }
    const viewKey = JSON.stringify([effectiveCenter, effectiveZoom]);
    if (lastViewRef.current === viewKey) {
      return;
    }
    lastViewRef.current = viewKey;

    if (effectiveCenter !== null) {
      map.setView([effectiveCenter.latitude, effectiveCenter.longitude], effectiveZoom ?? DEFAULT_ZOOM);
    } else {
      map.setView([DEFAULT_CENTER.latitude, DEFAULT_CENTER.longitude], effectiveZoom ?? DEFAULT_ZOOM);
    }
  }, [map, effectiveCenter, effectiveZoom]);

  const markersJson = useMemo(() => JSON.stringify(markers), [markers]);
  useEffect(() => {
    if (!map) {
      return;
    }
    map.clearMarkers();
    for (const marker of JSON.parse(markersJson) as MapMarker[]) {
      map.addMarker(
        marker.markerId,
        [marker.latLng.lat, marker.latLng.lng],
        marker.content ?? undefined,
        marker.markerIconUrl,
        marker.iconSize,
        marker.iconAnchor,
        marker.popupAnchor,
      );
    }
  }, [map, markersJson]);

  const circlesJson = useMemo(() => JSON.stringify(circles), [circles]);
  useEffect(() => {
    if (!map) {
      return;
    }
    map.clearCircles();
    for (const circle of JSON.parse(circlesJson) as MapCircle[]) {
      map.addCircle(
        [circle.latLng.lat, circle.latLng.lng],
        circle.radius,
        circle.color,
        circle.opacity ?? 1,
        circle.fillColor ?? '',
        circle.fillOpacity ?? 0,
      );
    }
  }, [map, circlesJson]);

  const pathsJson = useMemo(() => JSON.stringify(paths), [paths]);
  useEffect(() => {
    if (!map) {
      return;
    }
    map.clearPaths();
    for (const path of JSON.parse(pathsJson) as MapPath[]) {
      map.addPath(
        path.points.map((point) => [point.lat, point.lng]),
        path.options,
      );
    }
  }, [map, pathsJson]);

  const lastDisableControlsRef = useRef(false);
  useEffect(() => {
    if (!map) {
      return;
    }
    if (disableControls !== lastDisableControlsRef.current) {
      map.setDisableControls(disableControls);
      lastDisableControlsRef.current = disableControls;
    }
  }, [map, disableControls]);

  const contextMenuItemsJson = useMemo(() => JSON.stringify(contextMenuItems), [contextMenuItems]);
  useEffect(() => {
    if (!map) {
      return;
    }
    map.clearContextMenuItems();
    map.addContextMenuItems(JSON.parse(contextMenuItemsJson) as ContextMenuItem[]);
  }, [map, contextMenuItemsJson]);

  const tileLayersJson = useMemo(() => JSON.stringify(tileLayers), [tileLayers]);
  useEffect(() => {
    if (!map) {
      return;
    }
    map.setTileLayers(JSON.parse(tileLayersJson) as TileLayer[]);
  }, [map, tileLayersJson]);

  const agencyLayersJson = useMemo(() => JSON.stringify(agencyLayers), [agencyLayers]);
  useEffect(() => {
    if (!map) {
      return;
    }
    void map.setGeoJsonLayers(JSON.parse(agencyLayersJson) as MapLayer[]);
  }, [map, agencyLayersJson]);

  useEffect(() => {
    if (map && hideBuiltInLayerControl) {
      map.hideBuiltInLayerControl();
    }
  }, [map, hideBuiltInLayerControl]);

  return <div id="map" ref={containerRef} className="leaflet-map z-1" style={{ width: '100%', height: '100%' }}></div>;
}
